// /app/frontend/src/components/RoomMemoryBadge.jsx
// PATCH B: Room Memory - Shows visit count and last visited for a specific room

import React from 'react';
import { Clock, Repeat } from 'lucide-react';
import { useRoomParticipation } from '../hooks/useRoomMemory';

/**
 * RoomMemoryBadge - Inline badge showing user's history with a room
 * 
 * Features:
 * - Shows times visited
 * - Shows "last visited" in human-readable form
 * - Subtle, non-intrusive design
 * - Private to user only
 * 
 * @param {string} roomKey - The room slug/key
 */
export default function RoomMemoryBadge({ roomKey }) {
  const { participation, hasVisited, loading } = useRoomParticipation(roomKey);

  // Don't show anything if user has never visited
  if (loading || !hasVisited || !participation) {
    return null;
  }

  const timesJoined = participation.times_joined || 0;
  const lastVisitedText = participation.last_visited_text || '';

  // Only show if they've visited more than once
  if (timesJoined <= 1) {
    return null;
  }

  return (
    <div 
      className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 px-2 py-1 rounded-full bg-gray-50 dark:bg-slate-800/50"
      data-testid="room-memory-badge"
    >
      <Repeat className="w-3 h-3" />
      <span>
        {timesJoined} visits
      </span>
      {lastVisitedText && (
        <>
          <span className="text-gray-300 dark:text-gray-600">•</span>
          <Clock className="w-3 h-3" />
          <span>{lastVisitedText}</span>
        </>
      )}
    </div>
  );
}

/**
 * RoomMemorySummary - Larger card showing room history summary
 * For use in profile or dedicated history view
 */
export function RoomMemorySummary({ roomKey }) {
  const { participation, hasVisited, loading } = useRoomParticipation(roomKey);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-24"></div>
      </div>
    );
  }

  if (!hasVisited || !participation) {
    return (
      <div className="text-sm text-gray-400 dark:text-gray-500">
        First time here? Welcome!
      </div>
    );
  }

  const timesJoined = participation.times_joined || 0;
  const lastVisitedText = participation.last_visited_text || '';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-sm">
        <Repeat className="w-4 h-4 text-rose-500" />
        <span className="text-gray-700 dark:text-gray-300">
          You&apos;ve joined <span className="font-semibold">{timesJoined}</span> time{timesJoined !== 1 ? 's' : ''}
        </span>
      </div>
      {lastVisitedText && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Clock className="w-3 h-3" />
          <span>Last visited: {lastVisitedText}</span>
        </div>
      )}
    </div>
  );
}
